import { User } from 'lucide-react';

/**
 * Circular avatar (user image), aligned with @ann/web-components Avatar.
 * Border maps to ann `surface.neutral.focus` → #BABABA.
 *
 * Sizes (ann spacing, 4px base): sm 4x, md 6x, lg 8x, xl 12x, xxl 16x.
 * Custom size: omit `size` and pass Tailwind classes via `className` (e.g. `h-[72px] w-[72px]`).
 *
 * When `src` is missing, pass `initials` (e.g. `"AB"`) for a text fallback; style with `className`
 * (`bg-*`, `text-*`, `border-*`).
 *
 * With neither `src` nor `initials`, pass `placeholder` to show a generic icon (people known only by
 * email). Precedence: `src` > `initials` > `placeholder` > empty circle.
 */

const SIZE_CLASSES = {
  sm: 'h-4 w-4 min-h-[1rem] min-w-[1rem]',
  md: 'h-6 w-6 min-h-[1.5rem] min-w-[1.5rem]',
  lg: 'h-8 w-8 min-h-[2rem] min-w-[2rem]',
  xl: 'h-12 w-12 min-h-[3rem] min-w-[3rem]',
  xxl: 'h-16 w-16 min-h-[4rem] min-w-[4rem]',
};

const ICON_SIZES = { sm: 10, md: 14, lg: 18, xl: 26, xxl: 34 };

/**
 * @param {object} props
 * @param {'xxl'|'xl'|'lg'|'md'|'sm'} [props.size='md']
 * @param {string} [props.src]
 * @param {string} [props.alt='avatar'] — Used as `aria-label` when showing `initials` only.
 * @param {string} [props.initials] — Up to 2 characters when `src` is absent.
 * @param {boolean} [props.placeholder] — Shows a generic icon when `src` and `initials` are absent (`alt` is the `aria-label`).
 * @param {import('lucide-react').LucideIcon} [props.placeholderIcon] — Icon for the placeholder (default `User`).
 * @param {string} [props.className] — For custom sizing, use utilities (`h-*`, `w-*`, etc.).
 */
const Avatar = ({
  size = 'md',
  src,
  alt = 'avatar',
  initials,
  placeholder = false,
  placeholderIcon: PlaceholderIcon = User,
  className = '',
}) => {
  const sizeClass = SIZE_CLASSES[size] ?? SIZE_CLASSES.md;
  const hasSrc = typeof src === 'string' && src.trim().length > 0;
  const trimmedInitials = typeof initials === 'string' ? initials.trim() : '';
  const hasInitials = Boolean(trimmedInitials) && !hasSrc;
  const displayInitials = hasInitials ? trimmedInitials.slice(0, 2).toUpperCase() : '';

  const showPlaceholder = placeholder && !hasSrc && !hasInitials;

  return (
    <div
      role={hasInitials || showPlaceholder ? 'img' : undefined}
      aria-label={hasInitials || showPlaceholder ? alt : undefined}
      className={[
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full',
        'border border-[#BABABA] dark:border-gray-500',
        !hasSrc && !hasInitials ? 'bg-surface-muted' : '',
        sizeClass,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {hasSrc ? (
        <img src={src} alt={alt} className="h-full w-full object-cover" loading="lazy" decoding="async" />
      ) : hasInitials ? (
        <span className="select-none font-semibold tabular-nums leading-none" aria-hidden="true">
          {displayInitials}
        </span>
      ) : showPlaceholder ? (
        <PlaceholderIcon
          size={ICON_SIZES[size] ?? ICON_SIZES.md}
          strokeWidth={1.8}
          className="text-content-secondary"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
};

export default Avatar;
